import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Progress,
  Alert,
  Badge,
  Input,
  FormGroup,
  Label,
  Table,
} from 'reactstrap';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import { PageHeader } from '../../components/PageHeader';
import { QueryLoadingState, QueryErrorState } from '../../components/QueryStates';
import { RelativeTime } from '../../components/RelativeTime';
import { useApiClient } from '../../api/client.jsx';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import { ossReportFixture } from './fixture';
import { createDebugLogger } from '../../utils/debug';

const dlog = createDebugLogger('oss');

const fmtEur = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });

const COUNTRY_COLORS = ['#3f6ad8', '#3ac47d', '#fd7e14', '#dc3545', '#6f42c1', '#20c997', '#e83e8c', '#17a2b8'];

export function OssReportPage() {
  const [selectedQuarter, setSelectedQuarter] = useState('');

  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();
  const query = useQuery({
    queryKey: ['oss', fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return ossReportFixture();
      }
      // Core liefert zwei separate Endpoints. /quarter erwartet year + quarter
      // als Query-Params (Pflicht, sonst 400 validation_error). /threshold
      // erlaubt optional year (default: aktuelles Jahr).
      const now = new Date();
      const year = now.getFullYear();
      const quarterNum = Math.floor(now.getMonth() / 3) + 1;
      const [threshold, currentQuarterRes] = await Promise.all([
        fetch(`/v1/admin/oss-report/threshold?year=${year}`),
        fetch(`/v1/admin/oss-report/quarter?year=${year}&quarter=${quarterNum}`),
      ]);
      dlog('threshold', threshold, 'quarter', currentQuarterRes);
      // Wir bekommen NUR das aktuelle Quartal — die UI hatte mehrere
      // Quartale fürs Drilldown vorgesehen, aktuell nur das eine.
      // Backend liefert `byCountry` nicht garantiert (leeres Quartal) —
      // wir normalisieren auf `[]` damit forEach/map in der View nicht
      // crasht (ERRORs.md Error-01, 2026-06-18).
      const quarterShape = currentQuarterRes?.quarter ?? currentQuarterRes ?? null;
      const quarters = quarterShape
        ? [{
            quarter: `${year}-Q${quarterNum}`,
            ...quarterShape,
            byCountry: quarterShape.byCountry ?? [],
            total: quarterShape.total ?? 0,
            totalVat: quarterShape.totalVat ?? 0,
          }]
        : [];
      // CORE /oss-report/threshold liefert snake_case
      // (eu_b2c_revenue_minor / threshold_minor / over_threshold); die View
      // liest ytdGross/limit/reached. Ohne dieses Mapping → NaN €. Tolerant
      // gegenüber beiden Shapes (Fixture nutzt bereits camelCase).
      const ytdGross = threshold?.ytdGross ?? threshold?.eu_b2c_revenue_minor ?? 0;
      const limit = threshold?.limit ?? threshold?.threshold_minor ?? 0;
      const normThreshold = {
        year: threshold?.year ?? year,
        ytdGross,
        limit,
        reached: threshold?.reached ?? threshold?.over_threshold ?? (limit > 0 && ytdGross >= limit),
        remaining: Math.max(0, limit - ytdGross),
      };
      return {
        threshold: normThreshold,
        quarters,
        generatedAt: threshold?.generatedAt ?? currentQuarterRes?.generatedAt ?? new Date().toISOString(),
      };
    },
  });

  if (query.isLoading) {
    return (
      <div>
        <PageHeader title="OSS-Report (§ 18 UStG)" />
        <QueryLoadingState />
      </div>
    );
  }
  if (query.isError) {
    return (
      <div>
        <PageHeader title="OSS-Report (§ 18 UStG)" />
        <QueryErrorState error={query.error} onRetry={() => query.refetch()} />
      </div>
    );
  }

  const data = query.data;
  const { threshold, quarters } = data;
  const currentQuarter = selectedQuarter || quarters[quarters.length - 1]?.quarter;
  const quarter = quarters.find((q) => q.quarter === currentQuarter) ?? quarters[quarters.length - 1];

  // Stacked-Bar Chart-Daten: pro Quartal eine Reihe, pro Country eine Säule
  const chartData = quarters.map((q) => {
    const row = { quarter: q.quarter };
    q.byCountry.forEach((c) => {
      row[c.countryCode] = c.gross / 100; // EUR statt Cent
    });
    return row;
  });

  const ytdPct = Math.min(100, (threshold.ytdGross / threshold.limit) * 100);
  const thresholdReached = threshold.reached;

  return (
    <div>
      <PageHeader
        title="OSS-Report (§ 18 UStG)"
        subtitle={`Intra-EU-B2C-Umsatz · One-Stop-Shop Meldung ans BZSt · Stand ${new Date(data.generatedAt).toLocaleDateString('de-DE')}`}
      />

      <Row className="g-3 mb-3">
        <Col md="6">
          <Card className="h-100">
            <CardBody>
              <CardTitle tag="h5" className="mb-1">YTD-Threshold {threshold.year}</CardTitle>
              <p className="text-muted small mb-3">
                Bei Überschreitung von <code>{fmtEur.format(threshold.limit / 100)}</code> innerhalb
                eines Kalenderjahres greift die OSS-Pflicht — Umsatz wird im Verbrauchsland
                versteuert (nicht in DE).
              </p>

              <div className="d-flex justify-content-between mb-1">
                <span className="fw-bold display-6">{fmtEur.format(threshold.ytdGross / 100)}</span>
                <span className="text-muted">von {fmtEur.format(threshold.limit / 100)}</span>
              </div>
              <Progress
                value={ytdPct}
                color={thresholdReached ? 'danger' : ytdPct > 80 ? 'warning' : 'success'}
                animated={!thresholdReached}
              />
              <div className="d-flex justify-content-between small mt-1">
                <span className="text-muted">{ytdPct.toFixed(1)} %</span>
                <span className="text-muted">
                  noch {fmtEur.format(threshold.remaining / 100)}
                </span>
              </div>

              {thresholdReached ? (
                <Alert color="danger" className="small mt-3 mb-0">
                  ⚠ <strong>Schwellwert erreicht.</strong> OSS-Pflicht ist aktiv —
                  vierteljährliche Meldung ans BZSt notwendig.
                </Alert>
              ) : ytdPct > 80 ? (
                <Alert color="warning" className="small mt-3 mb-0">
                  📊 80%-Marke überschritten — OSS-Anmeldung beim BZSt vorbereiten,
                  damit nahtlos umgestellt werden kann.
                </Alert>
              ) : null}
            </CardBody>
          </Card>
        </Col>

        <Col md="6">
          <Card className="h-100">
            <CardBody>
              <CardTitle tag="h5">Quartals-Umsatz (Brutto pro Land)</CardTitle>
              <div style={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="quarter" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v.toFixed(0)} €`} />
                    <Tooltip formatter={(v) => fmtEur.format(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {(quarter?.byCountry ?? []).map((c, i) => (
                      <Bar
                        key={c.countryCode}
                        dataKey={c.countryCode}
                        stackId="oss"
                        fill={COUNTRY_COLORS[i % COUNTRY_COLORS.length]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <CardTitle tag="h5" className="mb-0">
              Quartals-Report
              <Badge color="secondary" pill className="ms-2">{quarter?.quarter}</Badge>
            </CardTitle>
            <FormGroup className="mb-0 d-flex align-items-center gap-2">
              <Label className="mb-0 small text-muted">Quartal:</Label>
              <Input
                type="select"
                bsSize="sm"
                value={currentQuarter}
                onChange={(e) => setSelectedQuarter(e.target.value)}
                style={{ maxWidth: 160 }}
              >
                {quarters.map((q) => (
                  <option key={q.quarter} value={q.quarter}>{q.quarter}</option>
                ))}
              </Input>
            </FormGroup>
          </div>

          <Table size="sm" className="mb-0 align-middle">
            <thead>
              <tr>
                <th>Land</th>
                <th className="text-end">VAT-Rate</th>
                <th className="text-end">Brutto</th>
                <th className="text-end">Netto</th>
                <th className="text-end">USt-Betrag</th>
              </tr>
            </thead>
            <tbody>
              {(quarter?.byCountry ?? []).map((c) => (
                <tr key={c.countryCode}>
                  <td>
                    <code className="me-2">{c.countryCode}</code>
                    {c.countryName}
                  </td>
                  <td className="text-end">{c.vatRate} %</td>
                  <td className="text-end">{fmtEur.format(c.gross / 100)}</td>
                  <td className="text-end">{fmtEur.format(c.net / 100)}</td>
                  <td className="text-end fw-semibold">{fmtEur.format(c.vatAmount / 100)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="fw-bold">
                <td colSpan="2">Summe</td>
                <td className="text-end">{fmtEur.format((quarter?.total ?? 0) / 100)}</td>
                <td className="text-end">{fmtEur.format(((quarter?.total ?? 0) - (quarter?.totalVat ?? 0)) / 100)}</td>
                <td className="text-end">{fmtEur.format((quarter?.totalVat ?? 0) / 100)}</td>
              </tr>
            </tfoot>
          </Table>
        </CardBody>
      </Card>

      <p className="text-muted small mt-2 mb-0 text-end">
        Stand: <RelativeTime iso={data.generatedAt} />
      </p>
    </div>
  );
}
